import { proposalPdfGenerator, quoteToClientPdf } from '@/modules/client-delivery/pdf'
import type { QuoteDocument, QuoteLineItem } from '../types'
import { quoteRepository, QuoteRepository } from './quote-repository'

export class QuoteService {
  private readonly repository: QuoteRepository

  constructor(repository: QuoteRepository = quoteRepository) {
    this.repository = repository
  }

  list(): QuoteDocument[] {
    return this.repository.listAll()
  }

  getById(id: string): QuoteDocument | null {
    return this.repository.findById(id)
  }

  createDraft(): QuoteDocument {
    return this.repository.createDraft()
  }

  save(doc: QuoteDocument): QuoteDocument {
    return this.repository.save(doc)
  }

  duplicate(id: string): QuoteDocument {
    return this.repository.duplicate(id)
  }

  clear(id: string): QuoteDocument {
    return this.repository.clearItems(id)
  }

  addManualItem(id: string, description: string, quantity: number, salePrice: number) {
    return this.repository.addManualItem(id, description, quantity, salePrice)
  }

  addProduct(id: string, product: { id: string; descricao: string; valorManual: number }) {
    return this.repository.addProductLine(id, product)
  }

  updateLine(
    id: string,
    lineId: string,
    patch: Partial<QuoteLineItem>,
    mode: 'components' | 'salePrice' | 'purchase' | 'profit' = 'components',
  ) {
    return this.repository.updateLine(id, lineId, patch, mode)
  }

  removeLine(id: string, lineId: string) {
    return this.repository.removeLine(id, lineId)
  }

  duplicateLine(id: string, lineId: string) {
    return this.repository.duplicateLine(id, lineId)
  }

  reorder(id: string, from: number, to: number) {
    return this.repository.reorderLines(id, from, to)
  }

  updateMeta(id: string, patch: Parameters<QuoteRepository['updateMeta']>[1]) {
    return this.repository.updateMeta(id, patch)
  }

  prepareSnapshot(id: string) {
    return this.repository.prepareSnapshot(id)
  }

  /** Generates a client-facing PDF from the saved quote (no internal cost fields). */
  preparePdf(id: string) {
    const quote = this.prepareSnapshot(id)
    const document = quoteToClientPdf(quote)
    const pdf = proposalPdfGenerator.generateFromClient(document)
    return {
      ok: true as const,
      message: 'PDF gerado com sucesso',
      quote,
      filename: pdf.filename,
      blob: pdf.blob,
      bytes: pdf.bytes,
      document,
    }
  }
}

export const quoteService = new QuoteService()
